import { Signer } from "@aws-amplify/core"
import "maplibre-gl/dist/maplibre-gl.css"
import React, { useCallback, useEffect, useState } from "react"
import ReactMapGL, {
  AttributionControl,
  MapRequest,
  NavigationControl,
} from "react-map-gl"
import { TemporaryCredentials } from "~/interfaces/entities/TemporaryCredentials"
import "./MapAWS.scss"

interface ViewPort {
  latitude: number
  longitude: number
  zoom: number
}

interface Props {
  credentials: TemporaryCredentials
  latitude: number
  longitude: number
  children?: React.ReactElement
}

const MapAWS: React.FC<Props> = ({
  credentials,
  latitude,
  longitude,
  children,
}) => {
  const [viewport, setViewport] = useState<ViewPort>({
    latitude: latitude,
    longitude: longitude,
    zoom: 13,
  })

  useEffect(() => {
    setViewport(previous => {
      return {
        ...previous,
        latitude,
        longitude,
        zoom: 13,
      }
    })
  }, [latitude, longitude])

  // see https://docs.aws.amazon.com/location/latest/developerguide/tutorial-maplibre-gl-js.html
  const transformRequest = useCallback(
    (url: string, resourceType: string) => {
      if (resourceType === "Style" && !url.includes("://")) {
        url = `https://maps.geo.${credentials?.region}.amazonaws.com/maps/v0/maps/${url}/style-descriptor`
      }

      if (url.includes("amazonaws.com")) {
        url = Signer.signUrl(url, {
          access_key: credentials?.key,
          secret_key: credentials?.secret,
          session_token: credentials?.sessionToken,
        })
      }
      return { url }
    },
    [credentials]
  )

  return (
    <ReactMapGL
      {...viewport}
      width="100%"
      height="100%"
      onViewportChange={setViewport}
      transformRequest={
        transformRequest as (url?: string, resourceType?: string) => MapRequest
      }
      mapStyle={credentials?.resource}
      scrollZoom={false}
      attributionControl={false}
    >
      {children}
      <AttributionControl
        compact={true}
        className="mapboxgl-ctrl-attrib-container"
      />
      <NavigationControl
        style={{
          right: 10,
          bottom: 50,
        }}
        showCompass={false}
        showZoom={true}
      />
    </ReactMapGL>
  )
}

export default React.memo(MapAWS)
